import React from 'react';

import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';

import { classnames } from '@lumapps/classnames';
import { mdiChevronDown, mdiChevronUp, mdiMenuRight } from '@lumapps/lumx/icons';
import { Icon, Offset, Orientation, Popover, PopoverProps } from '@lumapps/lumx/react';
import { MovingFocusProvider } from '@lumapps/lumx/react/utils';
import { useResponsive } from '@lumapps/responsive';
import { TreeOpenStateContext } from '@lumapps/utils/components/TreeOpenStateContext';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';
import { isComponentType } from '@lumapps/utils/types/isComponentType';

import { useDisableBlurWhileActivating } from '../../../hooks/useDisableBlurWhileActivating';
import { BASE_CLASSNAME, SUBMENU_OFFSET } from '../constants';
import { MenuContext, MenuContextOptions } from '../context';
import { Menu } from '../Menu';
import { MenuItem, MenuItemProps } from '../MenuItem';
import { MenuTriggerButton } from '../MenuTriggerButton';
import { MenuSkeleton } from '../Skeleton/MenuSkeleton';
import { useSubmenuOpenState } from '../state';
import { useMenuTrigger } from './useMenuTrigger';

export interface MenuTriggerProps {
    /**
     * `Menu.TriggerButton` & `Menu` children
     */
    children: React.ReactElement[];
    /**
     * Additional popover props.
     */
    popoverProps?: Partial<PopoverProps>;
    /**
     * Loading state. Puts the trigger button in loading mode and blocks the opening of the menu.
     */
    isLoading?: boolean;
    /**
     * Loading message (required when isLoading is `true`).
     */
    loadingMessage?: string;
    /**
     * Callback on open/close toggle.
     */
    onToggle?(isOpen: boolean): void;
    /**
     * Override the menu open state
     * **Warning**: You need to update this with the `onToggle` to keep the close on blur, close on escape, etc.
     */
    isOpen?: boolean;
    /**
     * Menu orientation
     *
     * */
    orientation?: Orientation;
}

/**
 * Menu trigger attaching the trigger button with the menu popup.
 *
 * @family Menus
 */
export const MenuTrigger = (props: MenuTriggerProps) => {
    const {
        children,
        popoverProps,
        onToggle,
        loadingMessage,
        isLoading: propIsLoading,
        orientation = 'vertical',
        isOpen,
    } = props;

    const [triggerChild, menuChild] = React.Children.toArray(children);

    const triggerRef = React.useRef<HTMLElement>(null) as React.MutableRefObject<HTMLElement>;
    const menuRef = React.useRef<HTMLElement>(null) as React.MutableRefObject<HTMLElement>;

    const hasMenu = isComponentType(Menu, menuChild);
    const hasMenuSkeleton = isComponentType(MenuSkeleton, menuChild);
    const menu = hasMenu || hasMenuSkeleton ? menuChild : undefined;
    const originalMenuId = menu?.props?.id;
    const menuId = React.useMemo(() => originalMenuId || uniqueId(BASE_CLASSNAME), [originalMenuId]);

    const trigger = isComponentType(MenuTriggerButton, triggerChild) ? triggerChild : undefined;
    const originalTriggerId = trigger?.props?.id;
    const triggerId = React.useMemo(
        () => originalTriggerId || uniqueId(`${BASE_CLASSNAME}-trigger`),
        [originalTriggerId],
    );
    const isSubMenu = trigger?.props.as === MenuItem;

    const context = React.useContext(MenuContext);
    const isHorizontal = context?.orientation === 'horizontal';

    // Mode replacing popup menu for submenus on small screens
    const { isSmall } = useResponsive();
    const isDisclosureSubMenu = !isHorizontal && isSubMenu && isSmall;

    // Menu is loading if `isLoading` prop is used or if the menu children is a `Menu.Skeleton`.
    const isLoading = propIsLoading || hasMenuSkeleton;

    const openState = useSubmenuOpenState(menuId, onToggle);

    React.useEffect(() => {
        if (isOpen === true) {
            openState.open();
        }
        if (isOpen === false) {
            openState.close();
        }
        // Do nothing when isOpen is undefined (keep the local state)
    }, [openState, isOpen]);

    const closeRoot: MenuContextOptions['closeRoot'] = React.useMemo(() => {
        if (context?.closeRoot) {
            return context?.closeRoot;
        }
        return () => {
            // Focus the trigger element if we had focus inside the menu
            if (menuRef.current?.contains(document.activeElement)) {
                triggerRef.current?.focus();
            }
            // Close the menu (only if still in the document)
            if (document.contains(menuRef.current)) {
                openState.close();
            }
        };
    }, [context?.closeRoot, openState]);

    const subContext = React.useMemo(
        (): MenuContextOptions => ({
            ...context,
            // Loading state
            isLoading,
            // Menu orientation
            orientation,
            // Define callback to close the root/topmost menu
            closeRoot,
            // Not root menu
            isRoot: context?.isRoot === undefined,
            // Set menu variant
            menuVariant: { size: 'm', orientation, theme: 'light' },
            type: 'Menu',
        }),
        [closeRoot, context, isLoading, orientation],
    );

    // Adapt offset on submenu to align menu items across menus
    const subMenuOffset = isHorizontal
        ? popoverProps?.offset
        : {
              along: (popoverProps?.offset?.along || 0) + SUBMENU_OFFSET.along,
              away: (popoverProps?.offset?.away || 0) + SUBMENU_OFFSET.away,
          };
    const offset: Offset | undefined = isSubMenu ? subMenuOffset : popoverProps?.offset;

    // A caller opted into custom sizing (either explicit width/minWidth/maxWidth, or an explicit
    // `fitToAnchorWidth` override) — in that case, don't apply the default anchor-width fitting or the
    // default `xxl` max-width.
    const hasCustomSizing = Boolean(
        popoverProps?.width ||
            popoverProps?.minWidth ||
            popoverProps?.maxWidth ||
            popoverProps?.fitToAnchorWidth !== undefined,
    );

    const { autofocus, triggerEventProps, menuEventProps, submenuOpenState } = useMenuTrigger(
        menuId,
        triggerRef,
        trigger?.props,
        menuRef,
        openState,
        context,
        subContext,
    );

    const disableBlurWhileActivating = useDisableBlurWhileActivating();

    const subMenu =
        (hasMenu || hasMenuSkeleton) && openState.isOpen ? (
            <TreeOpenStateContext.Provider value={submenuOpenState}>
                <MenuContext.Provider value={subContext}>
                    {isLoading ? (
                        <MenuSkeleton
                            loadingMessage={loadingMessage}
                            {...(hasMenuSkeleton ? menu?.props : undefined)}
                        />
                    ) : (
                        <>
                            {!isDisclosureSubMenu && menu && (
                                <MovingFocusProvider options={{ direction: orientation, loopAround: true, autofocus }}>
                                    <Menu
                                        {...menu?.props}
                                        orientation={orientation}
                                        ref={mergeRefs([(menu as any).ref, menuRef])}
                                        aria-labelledby={triggerId}
                                        id={menuId}
                                        {...menuEventProps}
                                        {...disableBlurWhileActivating}
                                    />
                                </MovingFocusProvider>
                            )}
                            {isDisclosureSubMenu && menu && (
                                // TODO: animate open/close
                                <ul
                                    role="none"
                                    // eslint-disable-next-line lumapps/no-classname-strings
                                    className={`${BASE_CLASSNAME}-disclosure`}
                                    {...disableBlurWhileActivating}
                                >
                                    {menu?.props.children}
                                </ul>
                            )}
                        </>
                    )}
                </MenuContext.Provider>
            </TreeOpenStateContext.Provider>
        ) : null;

    const fitToAnchorWidth = popoverProps?.fitToAnchorWidth ?? ((!isSubMenu || isHorizontal) && !hasCustomSizing);
    const subMenuPopup = subMenu && openState.isOpen && (
        <Popover
            role="none"
            {...popoverProps}
            fitToAnchorWidth={fitToAnchorWidth}
            className={classnames(popoverProps?.className, `${BASE_CLASSNAME}-popover`, {
                [`${BASE_CLASSNAME}-popover--default-width`]: !hasCustomSizing,
            })}
            offset={offset}
            placement={popoverProps?.placement || (isSubMenu && !isHorizontal ? 'right-start' : 'bottom-start')}
            isOpen={openState.isOpen}
            anchorRef={popoverProps?.anchorRef || triggerRef}
            fitWithinViewportHeight
            // Managed internally (see useMenuTrigger)
            focusAnchorOnClose={false}
            // Managed internally (see useMenuTrigger)
            closeOnClickAway={false}
            // Managed internally (see useMenuTrigger), needed to stop the propagation of the escape key event
            onClose={noop}
        >
            {subMenu}
        </Popover>
    );

    // Mark trigger button as busy and close menu on blur
    const loadingProps = isLoading ? { 'aria-busy': true, onBlur: openState.close } : undefined;

    let subMenuProps: Partial<MenuItemProps> | undefined;
    if (isSubMenu) {
        const disclosureSubMenuIcon = openState.isOpen ? mdiChevronUp : mdiChevronDown;
        const subMenuIcon = isHorizontal ? mdiChevronDown : mdiMenuRight;

        subMenuProps = {
            after: (
                <>
                    <Icon icon={isDisclosureSubMenu ? disclosureSubMenuIcon : subMenuIcon} size="xs" />
                    {!isDisclosureSubMenu && subMenuPopup}
                </>
            ),
            afterItem: isDisclosureSubMenu && subMenu,
        };
    }

    if (!trigger) {
        return null;
    }

    const className = classnames(trigger.props.className, subMenuProps?.className);

    return (
        <>
            <MenuTriggerButton
                {...trigger.props}
                ref={mergeRefs([(trigger as any).ref, triggerRef])}
                id={triggerId}
                {...triggerEventProps}
                aria-haspopup="true"
                aria-expanded={(!isLoading && openState.isOpen) || false}
                aria-controls={openState.isOpen ? menuId : undefined}
                data-menu-id={menuId}
                {...loadingProps}
                {...subMenuProps}
                className={className}
            >
                {trigger.props.children}
            </MenuTriggerButton>
            {!isSubMenu && subMenuPopup}
        </>
    );
};
